import apiService from './api';

/**
 * Translation Service
 * Handles operations related to translations
 */
class TranslationService {
  /**
   * Get translations for a specific language
   * @param {string} language - Language code (en, es, fr, nl)
   * @returns {Promise} - Promise with translations
   */
  async getTranslationsForLanguage(language) {
    try {
      const response = await apiService.get(`/translations/${language}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching translations for ${language}:`, error);
      throw error;
    }
  }

  /**
   * Get all translations
   * @returns {Promise} - Promise with all translations
   */
  async getAllTranslations() {
    try {
      const response = await apiService.get('/translations');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching all translations:', error);
      throw error;
    }
  }

  /**
   * Get list of translations for management
   * @returns {Promise} - Promise with translations list
   */
  async getTranslationsList() {
    try {
      const response = await apiService.get('/translations/list');
      // Ensure we always return an array
      return Array.isArray(response?.data?.data) ? response.data.data : [];
    } catch (error) {
      console.error('Error fetching translations list:', error);
      // Return empty array on error instead of throwing
      return [];
    }
  }

  /**
   * Create a new translation
   * @param {Object} translationData - Translation data
   * @returns {Promise} - Promise with created translation
   */
  async createTranslation(translationData) {
    try {
      const response = await apiService.post('/translations', translationData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating translation:', error);
      throw error;
    }
  }

  /**
   * Update a translation
   * @param {string} id - Translation ID
   * @param {Object} translationData - Updated translation data
   * @returns {Promise} - Promise with updated translation
   */
  async updateTranslation(id, translationData) {
    try {
      const response = await apiService.put(`/translations/${id}`, translationData);
      return response.data.data;
    } catch (error) {
      console.error(`Error updating translation ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a translation
   * @param {string} id - Translation ID
   * @returns {Promise} - Promise with operation result
   */
  async deleteTranslation(id) {
    try {
      const response = await apiService.delete(`/translations/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting translation ${id}:`, error);
      throw error;
    }
  }

  /**
   * Generate JSON files from database translations
   * @returns {Promise} - Promise with operation result
   */
  async generateJsonFiles() {
    try {
      const response = await apiService.post('/translations/generate-json');
      return response.data.data;
    } catch (error) {
      console.error('Error generating JSON files:', error);
      throw error;
    }
  }

  /**
   * Import translations from JSON files
   * @returns {Promise} - Promise with operation result
   */
  async importTranslationsFromJson() {
    try {
      const response = await apiService.post('/translations/import-json');
      return response.data.data;
    } catch (error) {
      console.error('Error importing translations from JSON:', error);
      throw error;
    }
  }
}

export default new TranslationService();